/**
 * Dữ liệu mẫu và máy chủ giả của màn Vai trò nền tảng (ADR-0074, `G2-D1-19`).
 *
 * Đóng vai backend khi chưa đăng nhập Keycloak thật hoặc khi URL có `?mock=`.
 * Máy chủ giả **từ chối y như hợp đồng** — cùng mã HTTP, cùng `pointer` — để
 * nhánh lỗi chạy được khi chưa có backend:
 *
 * - Tạo vai trò mã `VAN_HANH` (trùng, `409 PLATFORM_ROLE_ALREADY_EXISTS` trỏ `/code`).
 * - Phân quyền có mã ngoài danh mục: `422 PERMISSION_NOT_AVAILABLE` trỏ `/permissions/{i}`.
 * - Xoá ba vai trò hệ thống, hoặc vai trò còn người giữ: `409 PLATFORM_ROLE_IN_USE`.
 * - Gán vai trò cho `ps-003`: lần lưu đầu nhận `412 ROLE_VERSION_CONFLICT`, vì "một quản trị
 *   khác" vừa đổi vai trò của người này ngay sau lúc ngăn đọc. Tải lại rồi lưu lần nữa thì qua.
 * - Tự thêm vai trò cho mình (`pa-mau`): `403 SELF_ROLE_CHANGE_FORBIDDEN`; chỉ gỡ bớt thì được.
 *
 * Có đủ nhánh xấu: ba vai trò hệ thống không sửa được, vai trò chưa có quyền nào, vai trò giữ mã
 * quyền backend đã bỏ, vai trò chưa ai dùng.
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây.
 */
import type { ApiError } from '../lib/errors';
import {
  TOI,
  coNhanSu,
  datVaiTroNenTang,
  soNhanSuGiuVaiTro,
  vaiTroNenTangCuaNhanSu,
} from './nhan-su-nen-tang.ts';
import { MAU_MA_VAI_TRO } from '../modules/vai-tro-nen-tang/nghiep-vu/vai-tro-nen-tang.ts';
import type {
  NguCanhNenTang,
  QuyenNenTang,
  TrangQuyenNenTang,
  TrangVaiTroNenTang,
  TaoVaiTroNenTang,
  VaiTroCuaNhanSu,
  VaiTroCuaNhanSuCoPhienBan,
  VaiTroNenTang,
} from '../modules/vai-tro-nen-tang/types';

const q = (code: string, nhom: QuyenNenTang['nhom'], mo_ta: string): QuyenNenTang => ({
  code,
  nhom,
  mo_ta,
});

/** Danh mục đúng theo enum `PlatformPermission` của tenant-service (ADR-0074 mục 2). */
export const DANH_MUC_QUYEN_NEN_TANG: QuyenNenTang[] = [
  // ADR-0083: danh mục model AI cấp nền tảng và bật/tắt theo tenant; backend xếp đầu danh mục.
  q('AI_MODEL.READ', 'MODEL_AI', 'Xem danh mục model AI và model từng tenant dùng được'),
  q('AI_MODEL.UPDATE', 'MODEL_AI', 'Thêm, sửa, bật, tắt model AI và tắt model cho từng tenant'),
  q('TENANT.READ', 'TENANT', 'Xem danh sách và hồ sơ tenant'),
  q('TENANT.CREATE', 'TENANT', 'Tạo hồ sơ tenant mới'),
  q('TENANT.UPDATE', 'TENANT', 'Sửa hồ sơ, gói và tầng của tenant'),
  q('TENANT.PROVISION', 'TENANT', 'Duyệt hồ sơ và khởi động onboarding'),
  q('TENANT.SUSPEND', 'TENANT', 'Tạm ngưng và khôi phục tenant'),
  q('GOI.READ', 'GOI', 'Xem gói dịch vụ và kỳ thuê bao'),
  q('GOI.UPDATE', 'GOI', 'Sửa gói dịch vụ, hạn mức và ghi đè'),
  q('THUE_BAO.READ', 'GOI', 'Xem kỳ thuê bao của tenant'),
  q('THUE_BAO.CREATE', 'GOI', 'Mở kỳ thuê bao mới cho tenant'),
  q('THUE_BAO.UPDATE', 'GOI', 'Sửa kỳ thuê bao đang hiệu lực'),
  q('THUE_BAO.HUY', 'GOI', 'Huỷ kỳ thuê bao'),
  q('DICH_VU_XA.READ', 'GOI', 'Xem dịch vụ đang bật cho tenant'),
  q('DICH_VU_XA.UPDATE', 'GOI', 'Bật, tắt và ghi đè dịch vụ của tenant'),
  q('LICENSE.READ', 'LICENSE', 'Xem license on-premise và bản nháp'),
  q('LICENSE.ISSUE', 'LICENSE', 'Cấp và gia hạn license'),
  q('LICENSE.APPROVE', 'LICENSE', 'Duyệt bản nháp license'),
  q('LICENSE.REVOKE', 'LICENSE', 'Thu hồi license đã cấp'),
  q('VIEC.READ', 'VIEC', 'Xem hàng đợi việc và lượt onboarding'),
  q('VIEC.RETRY', 'VIEC', 'Chạy lại việc đang lỗi'),
  q('AUDIT_EVENTS.READ', 'AUDIT', 'Tra cứu nhật ký thao tác'),
  q('AUDIT_EVENTS.EXPORT', 'AUDIT', 'Xuất nhật ký ra tệp'),
  // ADR-0095 mục 10: màn Giá AI (giá mặc định, giá vốn, tỷ giá của audit-service).
  q('AI_PRICING.READ', 'AUDIT', 'Xem giá AI mặc định, giá vốn và tỷ giá'),
  q('AI_PRICING.UPDATE', 'AUDIT', 'Thêm phiên bản giá AI mặc định, giá vốn và tỷ giá'),
  q('PLATFORM_POLICY.READ', 'CHINH_SACH', 'Xem chính sách bảo mật và vận hành của nền tảng'),
  q('PLATFORM_POLICY.UPDATE', 'CHINH_SACH', 'Sửa chính sách nền tảng và mức log'),
  q('SECURITY_CONFIG.READ', 'BAO_MAT', 'Xem cấu hình bảo mật của từng tenant'),
  q('SECURITY_CONFIG.UPDATE', 'BAO_MAT', 'Sửa cấu hình bảo mật của tenant'),
  q('TENANT_CONTACT.READ', 'LIEN_HE', 'Xem đầu mối liên hệ của tenant'),
  q('TENANT_CONTACT.UPDATE', 'LIEN_HE', 'Sửa đầu mối liên hệ của tenant'),
  q('USER_CONFIG.READ', 'NHAN_SU', 'Xem danh sách nhân sự nền tảng'),
  q('USER_CONFIG.CREATE', 'NHAN_SU', 'Đề nghị tài khoản nhân sự nền tảng'),
  q('USER_CONFIG.APPROVE', 'NHAN_SU', 'Duyệt tài khoản nhân sự nền tảng'),
  q('USER_CONFIG.UPDATE', 'NHAN_SU', 'Sửa, khoá, mở khoá nhân sự nền tảng'),
  q('ROLE_CONFIG.READ', 'VAI_TRO', 'Xem vai trò và quyền của nhân sự nền tảng'),
  q('ROLE_CONFIG.UPDATE', 'VAI_TRO', 'Tạo, sửa, xoá vai trò nền tảng và gán cho nhân sự'),
  q('API_KEY.READ', 'KHOA_API', 'Xem khoá API của tenant'),
  q('API_KEY.UPDATE', 'KHOA_API', 'Tạo và thu hồi khoá API của tenant'),
  q('MODULE.READ', 'MODULE', 'Xem gói module đã nộp và lịch sử phiên bản'),
  q('MODULE.APPROVE', 'MODULE', 'Duyệt module lên Marketplace'),
];

const MOI_MA = DANH_MUC_QUYEN_NEN_TANG.map((x) => x.code);

/** Dòng gốc: `user_count` tính từ bảng gán, không lưu sẵn. */
interface VaiTroGoc {
  id: string;
  code: string;
  name: string;
  permissions: string[];
  is_system: boolean;
  created_at: string;
}

/** Ba vai trò hệ thống đúng như `V18` seed, cộng vài vai trò tự tạo để soát giao diện. */
let cacVaiTro: VaiTroGoc[] = [
  {
    id: 'pr-sa',
    code: 'PLATFORM_SUPER_ADMIN',
    name: 'Quản trị nền tảng',
    permissions: [...MOI_MA],
    is_system: true,
    created_at: '2026-10-01T02:00:00Z',
  },
  {
    id: 'pr-au',
    code: 'PLATFORM_AUDITOR',
    name: 'Kiểm toán',
    permissions: [
      'TENANT.READ',
      'AUDIT_EVENTS.READ',
      'AI_PRICING.READ',
      'SECURITY_CONFIG.READ',
      'TENANT_CONTACT.READ',
      'PLATFORM_POLICY.READ',
    ],
    is_system: true,
    created_at: '2026-10-01T02:00:00Z',
  },
  {
    id: 'pr-lo',
    code: 'PLATFORM_LICENSE_OPS',
    name: 'Phụ trách license',
    permissions: ['LICENSE.READ', 'LICENSE.ISSUE', 'LICENSE.APPROVE', 'LICENSE.REVOKE', 'TENANT.READ', 'GOI.READ'],
    is_system: true,
    created_at: '2026-10-01T02:00:00Z',
  },
  {
    id: 'pr-vh',
    code: 'VAN_HANH',
    name: 'Vận hành hạ tầng',
    permissions: ['TENANT.READ', 'VIEC.READ', 'VIEC.RETRY', 'AUDIT_EVENTS.READ'],
    is_system: false,
    created_at: '2026-10-12T03:20:00Z',
  },
  {
    id: 'pr-ho',
    code: 'HO_TRO_KHACH_HANG',
    name: 'Hỗ trợ khách hàng',
    permissions: ['TENANT.READ', 'TENANT_CONTACT.READ', 'GOI.READ'],
    is_system: false,
    created_at: '2026-10-14T07:05:00Z',
  },
  {
    id: 'pr-rong',
    code: 'CHUA_GAN_QUYEN',
    name: 'Vai trò mới, chưa gán quyền',
    permissions: [],
    is_system: false,
    created_at: '2026-10-20T01:00:00Z',
  },
  {
    // Giữ một mã backend đã bỏ: ngăn phân quyền phải nói ra và bỏ nó khi lưu
    id: 'pr-cu',
    code: 'VAI_TRO_CU',
    name: 'Vai trò cũ (có mã quyền đã bỏ)',
    permissions: ['TENANT.READ', 'BAO_CAO_CU.READ'],
    is_system: false,
    created_at: '2026-09-28T09:00:00Z',
  },
];

/**
 * Ai giữ vai trò nào **đọc từ kho nhân sự** (`mock/nhan-su-nen-tang.ts`), không giữ bảng riêng:
 * backend thật chỉ có một nguồn (`platform_user_role`), và hai kho tách rời từng làm cột Vai trò ở
 * bảng nhân sự trống trơn sau khi gán xong.
 *
 * Kho nhân sự lưu **mã** vai trò (`VAN_HANH`) vì đó là thứ API trả ra; API gán lại nhận **id**, nên
 * hai hàm dưới đổi qua lại. Dùng mã làm nguồn: id của vai trò vừa bị xoá thì không tra ngược được,
 * còn mã thì vẫn hiện ra để người đọc biết vai trò nào đã mất.
 */
const idTheoMa = (ma: string): string | undefined => cacVaiTro.find((r) => r.code === ma)?.id;
const maTheoId = (id: string): string | undefined => cacVaiTro.find((r) => r.id === id)?.code;

/** Id vai trò của một người, bỏ mã không còn vai trò tương ứng. */
const idDaGan = (userId: string): string[] =>
  vaiTroNenTangCuaNhanSu(userId)
    .map(idTheoMa)
    .filter((x): x is string => x !== undefined);

let soTao = 0;

/**
 * Nhân sự dùng để diễn ca `412`: "một quản trị khác vừa đổi vai trò của người này" ngay sau lúc
 * ngăn đọc. Dùng một id có thật trong kho nhân sự, nếu không lệnh gán sẽ chết ở bước 404 trước.
 */
const NS_XUNG_DOT = 'ns-003';

/** Đã diễn ca xung đột chưa — chỉ một lần, để lần lưu kế tiếp qua được. */
let daXungDot = false;

/** Cùng khuôn với `mock/vai-tro.ts`: `details` là mảng `errors` của Problem Details (ADR-0020). */
const loi = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  details,
});

const soNguoiGiu = (id: string) => {
  const ma = maTheoId(id);
  return ma === undefined ? 0 : soNhanSuGiuVaiTro(ma);
};

const kemSoNguoi = (r: VaiTroGoc): VaiTroNenTang => ({
  id: r.id,
  code: r.code,
  name: r.name,
  permissions: [...r.permissions].sort(),
  is_system: r.is_system,
  user_count: soNguoiGiu(r.id),
  created_at: r.created_at,
});

/** ETag giả: đủ để `If-Match` lệch thì phát hiện, không cần băm thật. */
const etagCua = (userId: string) => `"${[...idDaGan(userId)].sort().join(',') || 'rong'}"`;

function timVaiTro(id: string): VaiTroGoc {
  const r = cacVaiTro.find((x) => x.id === id);
  if (!r) throw loi('ROLE_NOT_FOUND', 404, 'Không tìm thấy vai trò nền tảng này.');
  return r;
}

/** Mã ngoài danh mục: `422` trỏ đúng từng vị trí sai, như tenant-service làm. */
function kiemQuyen(permissions: readonly string[]): void {
  const errors = permissions
    .map((ma, i) => ({ ma, i }))
    .filter(({ ma }) => !MOI_MA.includes(ma))
    .map(({ ma, i }) => ({
      pointer: `#/permissions/${i}`,
      detail: `Mã quyền ${ma} không có trong danh mục quyền nền tảng.`,
    }));
  if (errors.length > 0) {
    throw loi(
      'PERMISSION_NOT_AVAILABLE',
      422,
      `Có ${errors.length} mã quyền không thuộc danh mục quyền nền tảng.`,
      errors,
    );
  }
}

export const mayChuVaiTroNenTang = {
  /** `GET /api/v1/platform-roles` — sắp theo `code`, cursor là vị trí bắt đầu của trang sau. */
  danhSach({ limit, cursor }: { limit: number; cursor?: string }): TrangVaiTroNenTang {
    const sap = [...cacVaiTro].sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
    const tu = cursor ? Number(cursor) : 0;
    return {
      items: sap.slice(tu, tu + limit).map(kemSoNguoi),
      next_cursor: tu + limit < sap.length ? String(tu + limit) : null,
    };
  },

  /** `GET /api/v1/platform-permissions`. */
  danhMucQuyen(): TrangQuyenNenTang {
    return { items: structuredClone(DANH_MUC_QUYEN_NEN_TANG), next_cursor: null };
  },

  /** `POST /api/v1/platform-roles`. */
  tao(du: TaoVaiTroNenTang): VaiTroNenTang {
    if (!MAU_MA_VAI_TRO.test(du.code)) {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên không hợp lệ.', [
        { pointer: '#/code', detail: 'Mã chỉ gồm chữ in hoa, số và gạch dưới, bắt đầu bằng chữ.' },
      ]);
    }
    if (cacVaiTro.some((r) => r.code === du.code)) {
      throw loi('PLATFORM_ROLE_ALREADY_EXISTS', 409, `Mã ${du.code} đã dùng cho vai trò khác.`, [
        { pointer: '#/code', detail: `Mã ${du.code} đã dùng cho một vai trò nền tảng khác. Đặt mã khác.` },
      ]);
    }
    kiemQuyen(du.permissions ?? []);
    soTao += 1;
    const moi: VaiTroGoc = {
      id: `pr-moi-${soTao}`,
      code: du.code,
      name: du.name.trim(),
      permissions: [...(du.permissions ?? [])],
      is_system: false,
      created_at: new Date().toISOString(),
    };
    cacVaiTro = [...cacVaiTro, moi];
    return kemSoNguoi(moi);
  },

  /** `GET /api/v1/platform-roles/{id}`. */
  doc(id: string): VaiTroNenTang {
    return kemSoNguoi(timVaiTro(id));
  },

  /** `PATCH /api/v1/platform-roles/{id}` — vai trò hệ thống cũng đổi được tên. */
  doiTen(id: string, name: string): VaiTroNenTang {
    const r = timVaiTro(id);
    r.name = name.trim();
    return kemSoNguoi(r);
  },

  /** `PUT /api/v1/platform-roles/{id}/permissions`. */
  ganQuyen(id: string, permissions: string[]): VaiTroNenTang {
    const r = timVaiTro(id);
    if (r.is_system) {
      throw loi(
        'PLATFORM_ROLE_IN_USE',
        409,
        `Vai trò ${r.code} là vai trò hệ thống nên không đổi quyền được.`,
      );
    }
    kiemQuyen(permissions);
    r.permissions = [...permissions];
    return kemSoNguoi(r);
  },

  /** `DELETE /api/v1/platform-roles/{id}` — chặn vai trò hệ thống và vai trò còn người giữ. */
  xoa(id: string): void {
    const r = timVaiTro(id);
    if (r.is_system) {
      throw loi(
        'PLATFORM_ROLE_IN_USE',
        409,
        `Vai trò ${r.code} là vai trò hệ thống, giữ làm đường lùi nên không xoá được.`,
      );
    }
    const so = soNguoiGiu(id);
    if (so > 0) {
      throw loi('PLATFORM_ROLE_IN_USE', 409, `Vai trò này còn ${so} người đang giữ.`);
    }
    cacVaiTro = cacVaiTro.filter((x) => x.id !== id);
  },

  /** `GET /api/v1/platform-staff/{id}/roles` kèm `ETag`. */
  vaiTroCuaNhanSu(userId: string): VaiTroCuaNhanSuCoPhienBan {
    if (!coNhanSu(userId)) {
      throw loi('USER_NOT_FOUND', 404, 'Không tìm thấy nhân sự nền tảng này.');
    }
    return {
      user_id: userId,
      role_ids: idDaGan(userId),
      etag: etagCua(userId),
    };
  },

  /** `PUT /api/v1/platform-staff/{id}/roles` với `If-Match`. */
  ganVaiTroChoNhanSu(
    userId: string,
    roleIds: string[],
    etag: string,
  ): VaiTroCuaNhanSuCoPhienBan {
    if (!coNhanSu(userId)) {
      throw loi('USER_NOT_FOUND', 404, 'Không tìm thấy nhân sự nền tảng này.');
    }
    const hienCo = idDaGan(userId);

    // ps-003: một quản trị khác vừa đổi ngay sau lúc ngăn đọc — đúng một lần
    if (userId === NS_XUNG_DOT && !daXungDot) {
      daXungDot = true;
      datVaiTroNenTang(userId, ['VAN_HANH']);
      throw loi(
        'ROLE_VERSION_CONFLICT',
        412,
        'Tập vai trò của người này vừa được một yêu cầu khác thay đổi.',
      );
    }
    if (etag !== etagCua(userId)) {
      throw loi(
        'ROLE_VERSION_CONFLICT',
        412,
        'Tập vai trò của người này vừa được một yêu cầu khác thay đổi.',
      );
    }

    const la = roleIds
      .map((id, i) => ({ id, i }))
      .filter(({ id }) => !cacVaiTro.some((r) => r.id === id))
      .map(({ i }) => ({
        pointer: `#/role_ids/${i}`,
        detail: 'Vai trò này không còn tồn tại.',
      }));
    if (la.length > 0) {
      throw loi('ROLE_NOT_FOUND', 422, `Có ${la.length} vai trò không còn tồn tại.`, la);
    }

    // Tự thêm vai trò cho mình: chặn; chỉ gỡ bớt thì được (ADR-0004)
    if (userId === TOI) {
      const chiGoBot = roleIds.every((id) => hienCo.includes(id));
      if (!chiGoBot) {
        throw loi(
          'SELF_ROLE_CHANGE_FORBIDDEN',
          403,
          'Không tự thêm vai trò cho mình được.',
        );
      }
    }

    datVaiTroNenTang(
      userId,
      roleIds.map(maTheoId).filter((x): x is string => x !== undefined),
    );
    return {
      user_id: userId,
      role_ids: [...roleIds],
      etag: etagCua(userId),
    };
  },

  /**
   * `GET /api/v1/platform-context` — tài khoản mẫu là Platform Admin đầy quyền.
   *
   * Đúng hành vi ADR-0074 mục 5: chưa gán vai trò nào thì quyền vẫn tới từ realm role, nên màn
   * nào cũng mở — giống hệt trước ADR-0074.
   */
  nguCanh(vaiTroLop1: readonly string[]): NguCanhNenTang {
    const laAuditor = vaiTroLop1.includes('AUDITOR') && !vaiTroLop1.includes('PLATFORM_ADMIN');
    const tuRealmRole = laAuditor
      ? [
          'TENANT.READ',
          'AUDIT_EVENTS.READ',
          'AI_PRICING.READ',
          'SECURITY_CONFIG.READ',
          'TENANT_CONTACT.READ',
          'PLATFORM_POLICY.READ',
        ]
      : [...MOI_MA];
    const maDaGan = vaiTroNenTangCuaNhanSu(TOI);
    // ADR-0074 sửa lần 5: đã gán vai trò thì vai trò quyết tất, KHÔNG cộng dồn với realm role.
    const quyen = maDaGan.length
      ? maDaGan.flatMap((ma) => cacVaiTro.find((r) => r.code === ma)?.permissions ?? [])
      : tuRealmRole;
    return {
      user_id: TOI,
      roles: {
        platform: [...vaiTroLop1],
        nen_tang: [...maDaGan].sort(),
      },
      permissions: [...new Set(quyen)].sort(),
    };
  },
};

/** Vai trò của nhân sự nền tảng, cho ngăn gán ở màn Nhân sự nền tảng. */
export type { VaiTroCuaNhanSu };
